
import { Ship, Bullet, Asteroid, GameState } from './types';
import { getScaledValue, checkCollision, wrapPosition } from './utils';
import { Renderer } from './Renderer';
import { AsteroidManager } from './AsteroidManager';

export class AsteroidsGameEngine {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private state: GameState;
  private animationFrameId: number | null = null;
  private originalWidth: number;
  private originalHeight: number;
  private renderer: Renderer;
  private asteroidManager: AsteroidManager;
  private initialized: boolean = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get canvas context');
    }
    this.ctx = context;
    this.originalWidth = canvas.width;
    this.originalHeight = canvas.height;
    
    // Initialize renderer and asteroid manager
    this.renderer = new Renderer(this.ctx, canvas.width, canvas.height);
    this.asteroidManager = new AsteroidManager(canvas.width, canvas.height);

    // Initialize game state
    this.state = {
      ship: { 
        x: canvas.width/2, 
        y: canvas.height/2, 
        radius: getScaledValue(20, canvas.width, canvas.height), 
        angle: 0, 
        rotation: 0, 
        moving: false 
      },
      bullets: [],
      asteroids: this.asteroidManager.asteroids,
      score: 0,
      gameOver: false
    };
    
    console.log('AsteroidsGameEngine initialized with canvas size:', canvas.width, 'x', canvas.height);
  }

  public getState(): GameState {
    return this.state;
  }

  public resizeCanvas(width: number, height: number): void {
    // Store original positions as percentages
    const shipXPercent = this.state.ship.x / this.canvas.width;
    const shipYPercent = this.state.ship.y / this.canvas.height;
    
    // Update canvas size
    this.canvas.width = width;
    this.canvas.height = height;
    
    // Update renderer and asteroid manager
    this.renderer.updateCanvasSize(width, height);
    this.asteroidManager.resizeAsteroids(width, height);
    
    // Adjust ship position and size
    this.state.ship.x = shipXPercent * width;
    this.state.ship.y = shipYPercent * height;
    this.state.ship.radius = getScaledValue(20, width, height);
    
    // Update asteroids reference
    this.state.asteroids = this.asteroidManager.asteroids;
    
    console.log('Canvas resized to:', width, 'x', height);
  }

  public start(): void {
    if (this.animationFrameId === null) {
      console.log('Starting Asteroids game loop');
      this.initialized = true;
      this.gameLoop();
    }
  }

  public stop(): void {
    if (this.animationFrameId !== null) {
      console.log('Stopping Asteroids game loop');
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public rotateShipLeft(): void {
    this.state.ship.rotation = -0.05;
  }

  public rotateShipRight(): void {
    this.state.ship.rotation = 0.05;
  }

  public stopRotation(): void {
    this.state.ship.rotation = 0;
  }

  public startMoving(): void {
    this.state.ship.moving = true;
  }

  public stopMoving(): void {
    this.state.ship.moving = false;
  }

  public fireBullet(): void {
    const angle = this.state.ship.angle - Math.PI/2;
    this.state.bullets.push({
      x: this.state.ship.x + Math.cos(angle) * this.state.ship.radius,
      y: this.state.ship.y + Math.sin(angle) * this.state.ship.radius,
      dx: Math.cos(angle) * getScaledValue(5, this.canvas.width, this.canvas.height),
      dy: Math.sin(angle) * getScaledValue(5, this.canvas.width, this.canvas.height),
      life: 60 // frames
    });
  }

  private gameLoop = (): void => {
    if (this.state.gameOver) return;
    
    // Clear canvas
    this.renderer.clearCanvas();
    
    // Draw game objects
    this.renderer.drawShip(this.state);
    this.renderer.drawAsteroids(this.state);
    this.renderer.drawBullets(this.state);
    this.renderer.drawUI(this.state);
    
    // Update game state
    this.updateGameState();
    
    // Continue game loop
    this.animationFrameId = requestAnimationFrame(this.gameLoop);
  };

  private updateGameState(): void {
    this.updateShip();
    this.updateBullets();
    this.asteroidManager.updatePositions();
    
    // Keep asteroids reference in sync
    this.state.asteroids = this.asteroidManager.asteroids;
  }

  private updateShip(): void {
    // Update ship position and rotation
    if (this.state.ship.moving) {
      const angle = this.state.ship.angle - Math.PI/2;
      this.state.ship.x += Math.cos(angle) * getScaledValue(3, this.canvas.width, this.canvas.height);
      this.state.ship.y += Math.sin(angle) * getScaledValue(3, this.canvas.width, this.canvas.height);
    }
    
    this.state.ship.angle += this.state.ship.rotation;
    
    // Wrap ship around screen
    this.state.ship.x = wrapPosition(this.state.ship.x, this.canvas.width);
    this.state.ship.y = wrapPosition(this.state.ship.y, this.canvas.height);
  }
  
  private updateBullets(): void {
    for (let i = 0; i < this.state.bullets.length; i++) {
      const bullet = this.state.bullets[i];
      
      // Move bullet
      bullet.x += bullet.dx;
      bullet.y += bullet.dy;
      bullet.life--;
      
      // Remove bullet if off screen or too old
      if (bullet.x < 0 || bullet.x > this.canvas.width || 
          bullet.y < 0 || bullet.y > this.canvas.height || 
          bullet.life <= 0) {
        this.state.bullets.splice(i, 1);
        i--;
        continue;
      }
      
      // Check for collision with asteroids
      for (let j = 0; j < this.state.asteroids.length; j++) {
        const asteroid = this.state.asteroids[j];
        
        if (checkCollision(bullet.x, bullet.y, 2, asteroid.x, asteroid.y, asteroid.radius)) {
          // Hit asteroid
          this.state.score += 100;
          
          // Create smaller asteroids if large enough
          if (asteroid.radius > getScaledValue(15, this.canvas.width, this.canvas.height)) {
            const newAsteroids = this.asteroidManager.createSplitAsteroids(asteroid);
            this.state.asteroids.push(...newAsteroids);
          }
          
          // Remove asteroid and bullet
          this.state.asteroids.splice(j, 1);
          this.state.bullets.splice(i, 1);
          i--;
          break;
        }
      }
    }
  }
}
